import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const badgeVariants = cva(
  // Padding rides the --space-base density knob (same pattern as button.tsx
  // sizes) and the font carries a rem floor: at `touch` density the calc()
  // branch wins so badge text grows with the controls around it instead of
  // staying a tiny pill on a 64px-button shop-floor screen.
  'inline-flex items-center gap-1 rounded-full border px-[calc(var(--space-base)*2.5)] py-[calc(var(--space-base)*0.5)] text-[length:max(0.75rem,calc(var(--space-base)*2.4))] font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',

        // Live data accent (use sparingly, only on streaming widgets). Variant
        // key kept as `phosphor` (public API); colors use canonical `accent-live`.
        phosphor:
          'border-transparent bg-accent-live/15 text-accent-live ring-1 ring-inset ring-accent-live/40',

        // Soft tonal — semantic but low-saturation. Default for status pills.
        success:
          'border-transparent bg-[hsl(var(--state-running)/0.12)] text-[hsl(var(--state-running))] ring-1 ring-inset ring-[hsl(var(--state-running)/0.3)]',
        warning:
          'border-transparent bg-[hsl(var(--alarm-minor)/0.14)] text-[hsl(var(--alarm-minor))] ring-1 ring-inset ring-[hsl(var(--alarm-minor)/0.35)]',
        critical:
          'border-transparent bg-[hsl(var(--alarm-critical)/0.12)] text-[hsl(var(--alarm-critical))] ring-1 ring-inset ring-[hsl(var(--alarm-critical)/0.35)]',
        info:
          'border-transparent bg-[hsl(var(--alarm-advisory)/0.12)] text-[hsl(var(--alarm-advisory))] ring-1 ring-inset ring-[hsl(var(--alarm-advisory)/0.3)]',
        muted:
          'border-transparent bg-muted text-muted-foreground',

        // Solid alarm severities (ISA-18.2) — full color, for stripe-style
        // emphasis. Foreground is chosen per severity for WCAG AA contrast
        // against the solid fill in BOTH light and dark themes: bright fills
        // (amber/orange/blue/green) carry near-black text, deep fills (red)
        // carry white. The fill tokens are unchanged in light mode; dark-mode
        // fills were trimmed slightly (see tokens/colors.css) so white still
        // clears AA there.
        'alarm-critical':
          'border-transparent bg-[hsl(var(--alarm-critical))] text-white',
        'alarm-major':
          'border-transparent bg-[hsl(var(--alarm-major))] text-[hsl(0_0%_10%)]',
        'alarm-minor':
          'border-transparent bg-[hsl(var(--alarm-minor))] text-[hsl(0_0%_10%)]',
        'alarm-advisory':
          'border-transparent bg-[hsl(var(--alarm-advisory))] text-[hsl(0_0%_10%)]',

        // Solid equipment states (ISA-101)
        'state-running':
          'border-transparent bg-[hsl(var(--state-running))] text-[hsl(0_0%_10%)]',
        'state-idle':
          'border-transparent bg-[hsl(var(--state-idle))] text-white',
        'state-stopped':
          'border-transparent bg-[hsl(var(--state-stopped))] text-white',
        'state-error':
          'border-transparent bg-[hsl(var(--state-error))] text-white',
        'state-maintenance':
          'border-transparent bg-[hsl(var(--state-maintenance))] text-white',

        // ISA-101 high-performance-HMI operational-state ramp. The backend
        // (Isa101StateMapper) emits these token NAMES as `styleToken`; the
        // runtime maps the token straight onto the matching variant so it never
        // re-derives state colour. Per ISA-101 the normal/idle/out-of-service
        // baseline is DESATURATED (calm slate, no draw); the warning/abnormal/
        // critical/stale states carry the reserved saturated colour and pair
        // with white text for AA contrast against the saturated fill.
        'isa101-normal':
          'border-transparent bg-[hsl(var(--isa101-normal))] text-white',
        'isa101-idle':
          'border-transparent bg-[hsl(var(--isa101-idle))] text-[hsl(0_0%_10%)]',
        'isa101-out-of-service':
          'border-transparent bg-[hsl(var(--isa101-out-of-service))] text-[hsl(0_0%_10%)]',
        'isa101-warning':
          'border-transparent bg-[hsl(var(--isa101-warning))] text-[hsl(0_0%_10%)]',
        'isa101-abnormal':
          'border-transparent bg-[hsl(var(--isa101-abnormal))] text-[hsl(0_0%_10%)]',
        'isa101-critical':
          'border-transparent bg-[hsl(var(--isa101-critical))] text-white',
        'isa101-stale':
          'border-transparent bg-[hsl(var(--isa101-stale))] text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
